import {
    Box,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Spinner,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import imageCompression from 'browser-image-compression';
import { Button, Icon } from '../../ui';
import InventoryImageCropDialog from './InventoryImageCropDialog';
import InventoryItemImage from './InventoryItemImage';

export interface InventoryItemImageFieldProps {
    /** Current display URL (existing signed URL or local object URL preview). */
    previewUrl: string | null;
    onFileSelected: (file: File) => void | Promise<void>;
    onClear: () => void;
    isUploading?: boolean;
    isDisabled?: boolean;
    label?: string;
    /** When true, render without the "Image" form label header. */
    hideLabel?: boolean;
    /** When true, the preview fills the full column width instead of capping at 200px. */
    fullWidth?: boolean;
    /** When true, the preview is a square that fills its container width. */
    squarePreview?: boolean;
    /** Crop dialog title — `${name} · ${manufacturerPartNumber}`. */
    cropTitle: string;
    /** Shown under the Yaxle mark when the photo is missing or fails to load. */
    alt?: string | null;
}

const InventoryItemImageField = ({
    previewUrl,
    onFileSelected,
    onClear,
    isUploading = false,
    isDisabled = false,
    label = 'Image',
    hideLabel = false,
    fullWidth = false,
    squarePreview = false,
    cropTitle,
    alt,
}: InventoryItemImageFieldProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [compressError, setCompressError] = useState<string | null>(null);
    const [isCompressing, setIsCompressing] = useState(false);
    const [rawFile, setRawFile] = useState<File | null>(null);
    const [rawPreviewUrl, setRawPreviewUrl] = useState<string | null>(null);

    const closeCropDialog = () => {
        setRawPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });
        setRawFile(null);
        if (inputRef.current) inputRef.current.value = '';
    };

    const handlePick = (file: File | undefined) => {
        if (!file) return;
        setCompressError(null);
        setRawFile(file);
        setRawPreviewUrl(URL.createObjectURL(file));
    };

    const handleCropCancel = () => {
        closeCropDialog();
    };

    const handleCropConfirm = async (croppedFile: File) => {
        closeCropDialog();
        setCompressError(null);
        setIsCompressing(true);
        try {
            const compressed = await imageCompression(croppedFile, {
                maxSizeMB: 1.5,
                maxWidthOrHeight: 1600,
                useWebWorker: true,
            });
            const asFile = new File([compressed], croppedFile.name, {
                type: croppedFile.type || 'image/jpeg',
            });
            await onFileSelected(asFile);
        } catch {
            setCompressError('Failed to process image.');
        } finally {
            setIsCompressing(false);
        }
    };

    const busy = isUploading || isCompressing;
    /** The preview box and the action row share this cap so they stay flush. */
    const previewMaxW = squarePreview || fullWidth ? undefined : '200px';
    // With no photo, the placeholder itself is the upload control — no button row.
    const placeholderIsUpload = !previewUrl;
    const canUpload = !isDisabled && !busy;

    return (
        <FormControl>
            {!hideLabel && <FormLabel fontSize="sm">{label}</FormLabel>}
            <VStack align="stretch" spacing={2}>
                <Box
                    w="100%"
                    maxW={previewMaxW}
                    maxH={squarePreview ? undefined : fullWidth ? '320px' : '200px'}
                    minH={squarePreview ? undefined : '120px'}
                    aspectRatio={squarePreview ? 1 : undefined}
                    borderWidth="1px"
                    // Dashed dropzone while empty; a firm Smoke outline once a
                    // photo is in place.
                    borderStyle={placeholderIsUpload ? 'dashed' : 'solid'}
                    borderColor={placeholderIsUpload ? 'border.default' : 'border.strong'}
                    borderRadius="md"
                    bg="bg.surface"
                    overflow="hidden"
                    position="relative"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    {...(placeholderIsUpload
                        ? {
                              role: 'button',
                              tabIndex: canUpload ? 0 : -1,
                              'aria-label': 'Upload photo',
                              cursor: canUpload ? 'pointer' : 'not-allowed',
                              onClick: () => {
                                  if (canUpload) inputRef.current?.click();
                              },
                              onKeyDown: (event: React.KeyboardEvent) => {
                                  if (!canUpload) return;
                                  if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      inputRef.current?.click();
                                  }
                              },
                              _hover: canUpload
                                  ? { borderColor: 'brand.solid', bg: 'bg.subtle' }
                                  : undefined,
                              transition: 'border-color 0.15s ease, background 0.15s ease',
                          }
                        : {})}
                >
                    <Box
                        w="100%"
                        h={squarePreview ? '100%' : undefined}
                        minH={squarePreview ? undefined : '120px'}
                        maxH={squarePreview ? undefined : fullWidth ? '320px' : '200px'}
                    >
                        <InventoryItemImage
                            src={previewUrl}
                            alt={alt}
                            hint={
                                placeholderIsUpload && canUpload ? (
                                    <HStack spacing={1.5} color="fg.muted" mt={1}>
                                        <Icon name="upload" size="sm" />
                                        <Text fontSize="xs" fontWeight="medium">
                                            Click to upload
                                        </Text>
                                    </HStack>
                                ) : undefined
                            }
                        />
                    </Box>
                    {/* Actions ride on the photo so the field stays one row tall. */}
                    {previewUrl && (
                        <HStack
                            position="absolute"
                            bottom={0}
                            left={0}
                            right={0}
                            spacing={2}
                            px={2}
                            pb={2}
                        >
                            <Button
                                size="sm"
                                variant="outline"
                                flex={1}
                                bg="white"
                                _hover={{ bg: 'bg.surface' }}
                                leftIcon={<Icon name="upload" size="sm" />}
                                onClick={() => inputRef.current?.click()}
                                isDisabled={isDisabled || busy}
                            >
                                Replace
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                colorScheme="red"
                                flex={1}
                                bg="white"
                                _hover={{ bg: 'error.subtle' }}
                                leftIcon={<Icon name="trash" size="sm" />}
                                onClick={onClear}
                                isDisabled={isDisabled || busy}
                            >
                                Remove
                            </Button>
                        </HStack>
                    )}
                    {busy && (
                        <Box
                            position="absolute"
                            inset={0}
                            bg="blackAlpha.400"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Spinner color="white" size="md" />
                        </Box>
                    )}
                </Box>
                {compressError && (
                    <Text fontSize="sm" color="red.500">
                        {compressError}
                    </Text>
                )}
                <Input
                    ref={inputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    display="none"
                    onChange={(e) => handlePick(e.target.files?.[0])}
                />
            </VStack>
            {rawFile && rawPreviewUrl && (
                <InventoryImageCropDialog
                    isOpen
                    imageSrc={rawPreviewUrl}
                    fileName={rawFile.name}
                    fileType={rawFile.type}
                    title={cropTitle}
                    onCancel={handleCropCancel}
                    onConfirm={handleCropConfirm}
                />
            )}
        </FormControl>
    );
};

export default InventoryItemImageField;
